/**
 * ToolIntegrityPill — Inline per-tool trust indicator.
 *
 * Compact pill on tool execution cards. Hidden when no metadata (graceful
 * degradation — never show gray placeholder). Click expands inline detail.
 *
 * States:
 * - Verified: version + hash + publisher present → green dot
 * - Partial: some fields → amber dot
 * - Schema drift: hash mismatch → red dot
 * - Unknown: no metadata → component returns null (hidden)
 */

const React = (globalThis as any).React;
const { useState, useCallback } = React;

export interface ToolIntegrityData {
  tool_version?: string;
  tool_schema_hash?: string;
  tool_publisher?: string;
  tool_operation_class?: string;
  mcp_server_id?: string;
  schema_drift?: boolean;
}

export interface ToolIntegrityPillProps {
  toolId: string;
  integrity?: ToolIntegrityData | null;
  className?: string;
}

type IntegrityState = 'verified' | 'partial' | 'drift';

function deriveState(data: ToolIntegrityData): IntegrityState {
  if (data.schema_drift) return 'drift';
  if (data.tool_version && data.tool_schema_hash && data.tool_publisher) return 'verified';
  return 'partial';
}

const DOT_COLORS: Record<IntegrityState, string> = {
  verified: 'var(--color-success, #52c41a)',
  partial: 'var(--color-warning, #faad14)',
  drift: 'var(--color-error, #ff4d4f)',
};

const LABELS: Record<IntegrityState, string> = {
  verified: 'Verified tool',
  partial: 'Partially verified',
  drift: 'Schema changed since approval',
};

export function ToolIntegrityPill({ toolId, integrity, className }: ToolIntegrityPillProps) {
  const [expanded, setExpanded] = useState(false);

  if (!integrity || (!integrity.tool_version && !integrity.tool_publisher && !integrity.tool_schema_hash)) {
    return null;
  }

  const state = deriveState(integrity);
  const dotColor = DOT_COLORS[state];
  const label = LABELS[state];

  const displayText = state === 'drift'
    ? 'Schema changed since approval'
    : [
        integrity.tool_version ? `v${integrity.tool_version}` : null,
        integrity.tool_publisher || (state === 'partial' ? 'Unknown publisher' : null),
      ].filter(Boolean).join(' | ');

  const toggle = useCallback(() => setExpanded((e: boolean) => !e), []);

  const pillStyle: Record<string, string | number> = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 4,
    padding: '2px 8px',
    borderRadius: 'var(--border-radius-sm, 4px)',
    backgroundColor: 'var(--color-bg-subtle, rgba(0,0,0,0.04))',
    fontSize: 11,
    lineHeight: '16px',
    color: 'var(--color-text-secondary)',
    cursor: 'pointer',
    border: 'none',
    fontFamily: 'inherit',
    opacity: 1,
    transition: 'opacity 150ms ease-out',
  };

  const dotStyle: Record<string, string | number> = {
    width: 6,
    height: 6,
    borderRadius: '50%',
    backgroundColor: dotColor,
    flexShrink: 0,
  };

  const detailStyle: Record<string, string | number> = {
    marginTop: 4,
    padding: '6px 8px',
    backgroundColor: 'var(--color-bg-subtle, rgba(0,0,0,0.02))',
    borderRadius: 'var(--border-radius-sm, 4px)',
    fontSize: 11,
    color: 'var(--color-text-secondary)',
    lineHeight: 1.4,
  };

  return React.createElement('div', {
    'data-testid': `tool-integrity-${toolId}`,
    className: className || undefined,
  },
    React.createElement('button', {
      style: pillStyle,
      onClick: toggle,
      'aria-expanded': expanded,
      'aria-label': `Tool integrity: ${label}`,
      title: label,
    },
      React.createElement('span', { style: dotStyle }),
      React.createElement('span', null, displayText),
    ),
    expanded && React.createElement('div', { style: detailStyle },
      integrity.tool_version && React.createElement('div', null, `Version: ${integrity.tool_version}`),
      integrity.tool_publisher && React.createElement('div', null, `Publisher: ${integrity.tool_publisher}`),
      integrity.tool_schema_hash && React.createElement('div', null,
        `Schema: ${integrity.tool_schema_hash.substring(0, 12)}...`
      ),
      integrity.tool_operation_class && React.createElement('div', null,
        `Class: ${integrity.tool_operation_class}`
      ),
      integrity.mcp_server_id && React.createElement('div', null,
        `Server: ${integrity.mcp_server_id}`
      ),
    ),
  );
}
